<template>
  <div id="upload-documents-page" class="viewport viewport-with-top-nav viewport-compact">

    <TopNav></TopNav>

    <div class="display-1 my-3">{{$t('UploadPage.uploadDocumentPhotos')}}</div>
    <p v-html="$t('UploadPage.takePhotosOfIdCard')"></p>

    <div class="list-group list-group-escape-container mb-3">
      <div class="list-group-item list-group-item-action list-group-item-with-icon align-items-start"
           id="document-photo-1-upload"
           @click="chooseFiles('docPhoto1')"
      >
        <div>
          <div class="font-weight-bold">{{$t('UploadPage.documentPhoto1')}}</div>
          <div class="small text-muted" v-html="$t('UploadPage.idCardFront')"></div>
        </div>
        <input id="docPhoto1" type="file" @change="previewFiles($event, 'docPhoto1')" hidden accept=".png,.jpg,.jpeg">
        <div class="img-thumbnail-with-overlay ml-2">
          <canvas id="docPhoto1Canvas" class="img-thumbnail" style="width: 96px; height: 72px"></canvas>
          <template v-if="processingDocPhoto1">
            <div class="img-thumbnail-overlay">
              <div class="d-flex align-items-center">
                <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              </div>
            </div>
          </template>
          <template v-else-if="!docPhoto1Selected">
            <div class="img-thumbnail-overlay">
              <div class="d-flex align-items-center">
                <Icon icon-name="plus-outline" class="m-0 text-muted"></Icon>
              </div>
            </div>
          </template>
        </div>
      </div>
      <div class="list-group-item list-group-item-action list-group-item-with-icon align-items-start"
           id="document-photo-2-upload"
           @click="chooseFiles('docPhoto2')"
      >
        <div>
          <div class="font-weight-bold">{{$t('UploadPage.documentPhoto2')}}</div>
          <div class="small text-muted" v-html="$t('UploadPage.idCardBack')"></div>
        </div>
        <input id="docPhoto2" type="file" @change="previewFiles($event, 'docPhoto2')" hidden accept=".png,.jpg,.jpeg">

        <div class="img-thumbnail-with-overlay ml-2">
        <canvas id="docPhoto2Canvas" class="img-thumbnail" style="width: 96px; height: 72px"></canvas>
          <template v-if="processingDocPhoto2">
            <div class="img-thumbnail-overlay">
              <div class="d-flex align-items-center">
                <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              </div>
            </div>
          </template>
          <template v-else-if="!docPhoto2Selected">
            <div class="img-thumbnail-overlay">
              <div class="d-flex align-items-center">
                <Icon icon-name="plus-outline" class="m-0 text-muted"></Icon>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>

    <button class="btn btn-lg btn-primary btn-block mb-3"
            id="document-photo-upload-button"
            @click="upload"
            :disabled="buttonUploadDisabled"
    >
      <span v-if="uploading" >{{$t('UploadPage.uploading')}}</span>
      <span v-else>{{$t('UploadPage.upload')}}</span>
    </button>
    <div class="flex-grow-1"></div><!-- Fill the rest of the page-->

  </div>

</template>

<script>
  import Icon from '@shared/components/Icon'
  import TopNav from '@shared/components/TopNav'

  export default {
    name: 'UploadPage',
    components: { TopNav, Icon },
    data() {
      return {
        docPhoto1Selected: false,
        processingDocPhoto1: false,
        docPhoto2Selected: false,
        processingDocPhoto2: false,
        uploading: false
      }
    },
    computed: {
      buttonUploadDisabled() {
        return !(this.docPhoto1Selected || this.docPhoto2Selected) || this.uploading
      }
    },
    methods: {
      chooseFiles(fileUploadId) {
        document.getElementById(fileUploadId).click()
      },
      previewFiles(event, fileUploadId) {
        if (fileUploadId === 'docPhoto1') {
          this.processingDocPhoto1 = true
          this.handleImage(event, 'docPhoto1Canvas').then(() => {
            this.processingDocPhoto1 = false
            this.docPhoto1Selected = true
          })
        }

        if (fileUploadId === 'docPhoto2') {
          this.processingDocPhoto2 = true
          this.handleImage(event, 'docPhoto2Canvas').then(() => {
            this.processingDocPhoto2 = false
            this.docPhoto2Selected = true
          })
        }
      },
      resizeOriginalImageAndReturnCanvas(img) {
        const maxSizeOfLargestSide = 1500
        const largestSideSize = (img.width / img.height) > 1 ? (img) => img.width : (img) => img.height
        let ratio = maxSizeOfLargestSide / largestSideSize(img)
        if (ratio < 0.05) {
          ratio = 0.05
        }

        const resizedCanvas = document.createElement('canvas')
        const resizeCanvasCtx = resizedCanvas.getContext('2d')

        resizedCanvas.width = img.width * ratio
        resizedCanvas.height = img.height * ratio
        resizeCanvasCtx.drawImage(img, 0, 0, resizedCanvas.width, resizedCanvas.height)

        return resizedCanvas
      },
      handleImage(e, thumbnailCanvasId) {
        return new Promise((resolve, reject) => {
          const reader = new FileReader()
          const canvas = document.getElementById(thumbnailCanvasId)
          const ctx = canvas.getContext('2d')
          const resizeOriginalImageAndReturnCanvasFn = this.resizeOriginalImageAndReturnCanvas.bind(this)
          reader.onload = function(event) {
            const img = new Image()
            img.onload = function() {
              const resizedCanvas = resizeOriginalImageAndReturnCanvasFn(img)
              canvas.width = resizedCanvas.width
              canvas.height = resizedCanvas.height
              const imageData = resizedCanvas.getContext('2d').getImageData(0, 0, resizedCanvas.width, resizedCanvas.height)
              ctx.putImageData(imageData, 0, 0)
              resolve()
            }
            img.src = event.target.result
          }
          reader.readAsDataURL(e.target.files[0])
        })
      },
      markBeingUploaded() {
        this.uploading = this.processingDocPhoto1 = this.processingDocPhoto2 = true
      },
      doneUploading() {
        this.uploading = this.processingDocPhoto1 = this.processingDocPhoto2 = false
      },
      getAsJpegBlob(canvasId) {
        const mimeType = 'image/jpeg'
        const step = 0.1
        const minQuality = 0.2
        const maxFileSize = 1024 * 1024
        let quality = 0.8
        let byteString
        const canvas = document.getElementById(canvasId)
        let dataUri
        do {
          dataUri = canvas.toDataURL(mimeType, quality)
          if (dataUri.split(',')[0].indexOf('base64') >= 0) {
            byteString = atob(dataUri.split(',')[1])
          } else {
            byteString = unescape(dataUri.split(',')[1])
          }
          quality -= step
        } while (byteString.length > maxFileSize && quality > minQuality)

        if (byteString.length > maxFileSize) {
          throw new Error('Image is too large')
        }

        const mimeString = dataUri.split(',')[0].split(':')[1].split(';')[0]
        const arrayBuffer = new ArrayBuffer(byteString.length)
        const uint8Array = new Uint8Array(arrayBuffer)

        for (let i = 0; i < byteString.length; i++) {
          uint8Array[i] = byteString.charCodeAt(i)
        }

        return new Blob([uint8Array], { type: mimeString })
      },
      upload() {
        this.markBeingUploaded()

        this.docPhotosAsFormData()
            .then(this.submitPhotos)
            .then(() => this.$router.push({ name: 'customer-overview' }))
            .catch(this.doneUploading)
      },
      submitPhotos(formData) {
        return this.$http.post(`/api/applications/${this.$route.params.reference}/document/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
      },
      async docPhotosAsFormData() {
        const formData = new FormData()
        const ts = new Date().getTime()
        if (this.docPhoto1Selected) {
          formData.append('front', this.getAsJpegBlob('docPhoto1Canvas'), `front_${ts}.jpg`)
        }

        if (this.docPhoto2Selected) {
          formData.append('back', this.getAsJpegBlob('docPhoto2Canvas'), `back_${ts}.jpg`)
        }
        return formData
      }
    }
  }
</script>
